import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Plane, Loader2, Wind, Eye, Thermometer, Gauge, Cloud, AlertTriangle, Sun, Moon, CloudSun, CloudMoon, CloudRain, CloudDrizzle, CloudLightning, CloudFog, CloudSnow, Mountain, Droplets, Snowflake } from "lucide-react";

const catLabel = (code) => ({ VFR: "VFR", MVFR: "MVFR", IFR: "IFR", LIFR: "LIFR" }[code] || code || "—");

// Assess airborne moisture from temperature, dew point and humidity.
function moistureAssessment(m) {
  const t = m?.temperature_exact ?? m?.temperature;
  const dp = m?.dewpoint_exact ?? m?.dewpoint;
  if (t == null || dp == null) return null;
  const rh = m?.humidity;
  const spread = Math.round((t - dp) * 10) / 10;
  const freezing = t <= 0;
  const rhTxt = rh != null ? `, ${rh}% RH` : "";
  let icon = "sun", tone = "emerald", label = "Dry air";
  let detail = `Spread ${spread}°C${rhTxt} — low condensation risk.`;
  if (spread <= 1) {
    icon = "fog"; tone = freezing ? "sky" : "slate";
    label = freezing ? "Freezing fog likely" : "Fog likely";
    detail = `Spread ${spread}°C${rhTxt} — air near saturation. ${freezing ? "Freezing fog can deposit rime ice on airframe & props." : "Visibility can drop quickly."}`;
  } else if (spread <= 2.5) {
    icon = "fog"; tone = "slate";
    label = freezing ? "Freezing mist / low cloud" : "Mist / low cloud possible";
    detail = `Spread ${spread}°C${rhTxt} — near saturation. Patchy mist or low stratus possible.`;
  } else if (spread <= 5) {
    icon = "drop"; tone = "blue";
    label = "Condensation risk";
    detail = `Spread ${spread}°C${rhTxt} — humid. Dew/condensation may form on cool lenses, sensors & batteries.`;
  }
  if (freezing && dp <= 0 && spread <= 3) {
    icon = "ice"; tone = "sky";
    label = "Frost / ice risk";
    detail = `Sub-zero ${Math.round(t)}°C, dew point ${Math.round(dp)}°C — frost/ice may form on surfaces & props. Any precipitation falls as snow/ice.`;
  } else if (freezing) {
    detail += " At/below 0°C any precipitation falls as snow/ice.";
  }
  return { icon, tone, label, detail };
}

const MOISTURE_TONE = {
  emerald: "border-emerald-100 bg-emerald-50 text-emerald-700",
  slate: "border-slate-200 bg-slate-100 text-slate-700",
  blue: "border-blue-100 bg-blue-50 text-blue-700",
  sky: "border-sky-100 bg-sky-50 text-sky-700",
};
const MOISTURE_ICON = { fog: CloudFog, drop: Droplets, ice: Snowflake, rain: CloudRain, sun: Sun };

// Standard aviation pressure-altitude & density-altitude at field elevation.
function computeAltitudes(m, ap) {
  const elev = ap?.elevation;
  const qnh = m?.qnh;
  const oat = m?.temperature_exact ?? m?.temperature;
  if (elev == null || qnh == null || oat == null) return null;
  const qnhInHg = qnh / 33.8639;                       // hPa → inHg
  const pa = elev + (29.9213 - qnhInHg) * 1000;        // pressure altitude (ft)
  const isa = 15 - 1.98 * (pa / 1000);                 // ISA temp at PA (°C)
  const da = pa + 118.8 * (oat - isa);                 // density altitude (ft)
  return { pa: Math.round(pa), da: Math.round(da), elev: Math.round(elev), oat: Math.round(oat) };
}

function WeatherGlyph({ keyword, isDay, className }) {
  const k = (keyword || "").toLowerCase();
  let Icon = isDay ? Sun : Moon;
  if (/thunder|ts|lightning/.test(k)) Icon = CloudLightning;
  else if (/snow|sleet/.test(k)) Icon = CloudSnow;
  else if (/drizzle/.test(k)) Icon = CloudDrizzle;
  else if (/rain|shower/.test(k)) Icon = CloudRain;
  else if (/fog|mist|haze|smoke/.test(k)) Icon = CloudFog;
  else if (/overcast|broken|cloud/.test(k)) Icon = Cloud;
  else if (/scattered|few|partly/.test(k)) Icon = isDay ? CloudSun : CloudMoon;
  else if (/clear|sunny|fine|cavok/.test(k)) Icon = isDay ? Sun : Moon;
  return <Icon className={className} />;
}

function WxCompass({ dir, speed }) {
  const d = dir ?? 0;
  const showArrow = speed > 0 && dir != null;
  return (
    <div className="relative shrink-0" style={{ width: 116, height: 116 }} data-testid="wx-compass">
      <svg viewBox="0 0 120 120" className="w-full h-full">
        <circle cx="60" cy="60" r="56" fill="#f8fafc" stroke="#e2e8f0" strokeWidth="2" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
          const rad = (a - 90) * Math.PI / 180;
          const inner = a % 90 === 0 ? 46 : 50;
          return <line key={a} x1={60 + inner * Math.cos(rad)} y1={60 + inner * Math.sin(rad)} x2={60 + 55 * Math.cos(rad)} y2={60 + 55 * Math.sin(rad)} stroke="#cbd5e1" strokeWidth={a % 90 === 0 ? 2 : 1} />;
        })}
        <text x="60" y="16" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#64748b">N</text>
        <text x="106" y="64" textAnchor="middle" fontSize="10" fill="#94a3b8">E</text>
        <text x="60" y="112" textAnchor="middle" fontSize="10" fill="#94a3b8">S</text>
        <text x="14" y="64" textAnchor="middle" fontSize="10" fill="#94a3b8">W</text>
        {showArrow && (
          <g transform={`rotate(${d} 60 60)`}>
            <line x1="60" y1="20" x2="60" y2="34" stroke="#65a30d" strokeWidth="3" />
            <polygon points="60,42 53,30 67,30" fill="#65a30d" />
          </g>
        )}
        <circle cx="60" cy="60" r="19" fill="#0f172a" />
        <text x="60" y="59" textAnchor="middle" fill="#ffffff" fontSize="15" fontWeight="bold">{speed != null ? speed : "–"}</text>
        <text x="60" y="72" textAnchor="middle" fill="#a3e635" fontSize="8" fontWeight="bold">KT</text>
      </svg>
      {dir != null && <p className="text-center text-[11px] text-slate-500 -mt-1">{dir}°</p>}
    </div>
  );
}

function fmtTime(unix) {
  if (!unix) return "";
  try { return new Date(unix * 1000).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", timeZone: "Australia/Brisbane" }); }
  catch { return ""; }
}

const cloudWord = (a) => ({ FEW: "few clouds", SCT: "scattered cloud", BKN: "broken cloud", OVC: "overcast", NCD: "no cloud detected", NSC: "no significant cloud", CLR: "clear", SKC: "sky clear" }[a] || a);

function plainEnglish(m) {
  if (!m) return "";
  const parts = [];
  if (m.wind_speed != null) {
    if (m.wind_speed === 0) parts.push("wind calm");
    else parts.push(`wind from ${m.wind_dir ?? "variable"}° at ${m.wind_speed} knots${m.wind_gust ? `, gusting ${m.wind_gust} knots` : ""}`);
  }
  if (m.visibility != null) parts.push(m.visibility >= 9999 ? "visibility 10 km or more" : `visibility ${(m.visibility / 1000).toFixed(m.visibility % 1000 ? 1 : 0)} km`);
  if (m.clouds?.length) {
    const cl = m.clouds.map((c) => (c.height != null ? `${cloudWord(c.amount)} at ${c.height} ft` : cloudWord(c.amount))).join(", ");
    parts.push(cl);
  }
  if (m.temperature != null) parts.push(`temperature ${m.temperature}°C, dew point ${m.dewpoint ?? "?"}°C`);
  if (m.qnh != null) parts.push(`QNH ${m.qnh} hPa`);
  let s = parts.join(", ");
  s = s ? s.charAt(0).toUpperCase() + s.slice(1) + "." : "";
  if (m.code) s += ` Flight conditions ${catLabel(m.code)}.`;
  return s;
}

function RunwayDiagram({ runways, windDir, windSpeed }) {
  if (!runways?.length) return null;
  return (
    <div className="mt-4" data-testid="runway-diagram">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Runways &amp; wind</p>
      <div className="flex flex-wrap gap-3">
        {runways.map((r, i) => (
          <div key={`${r.id_l}-${r.id_h}`} className="w-32 border border-slate-200 rounded-lg p-2" data-testid={`runway-${r.id_l}-${r.id_h}`}>
            <div className="relative h-24 w-full bg-slate-50 rounded overflow-hidden">
              <span className="absolute top-0.5 left-1/2 -translate-x-1/2 text-[9px] text-slate-400">N</span>
              <div className="absolute left-1/2 top-1/2" style={{ transform: `translate(-50%,-50%) rotate(${r.hdg_l}deg)` }}>
                <div className="h-20 w-3 bg-slate-700 rounded-sm flex flex-col justify-between items-center py-0.5">
                  <span className="text-[8px] text-white font-bold leading-none" style={{ transform: `rotate(${-r.hdg_l}deg)` }}>{r.id_l}</span>
                  <span className="text-[8px] text-white font-bold leading-none" style={{ transform: `rotate(${-r.hdg_l}deg)` }}>{r.id_h}</span>
                </div>
              </div>
              {windSpeed > 0 && windDir != null && (
                <div className="absolute left-1/2 top-1/2" style={{ transform: `translate(-50%,-50%) rotate(${windDir}deg)` }} title={`Wind ${windDir}° ${windSpeed}kt`}>
                  <div className="h-24 w-0.5 bg-lime-500/80 relative">
                    <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-lime-600 text-[11px] leading-none">▲</span>
                  </div>
                </div>
              )}
            </div>
            <p className="text-[10px] text-center text-slate-700 font-medium mt-1">RWY {r.id_l}/{r.id_h}</p>
            <p className="text-[10px] text-center text-slate-400">in use {r.in_use} · HW {r.hwnd}kt · XW {r.xwnd}kt</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function MetarPanel({ icao, lat, lon, title = "METAR / TAF", compact = false, onResolved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let res;
      if (icao && icao.trim()) {
        try {
          res = await api.get("/metar", { params: { icao: icao.trim() } });
        } catch (e) {
          if (lat != null && lon != null) res = await api.get("/metar/nearest", { params: { lat, lon } });
          else throw e;
        }
      } else if (lat != null && lon != null) {
        res = await api.get("/metar/nearest", { params: { lat, lon } });
      } else { setLoading(false); setData(null); return; }
      setData(res.data);
      if (res.data.resolved && onResolved) onResolved(res.data.resolved);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load aviation weather");
      setData(null);
    } finally { setLoading(false); }
  }, [icao, lat, lon, onResolved]);

  useEffect(() => { load(); }, [load]);

  const ap = data?.airport;
  const m = data?.metar;
  const taf = data?.taf;
  const runways = data?.runways || [];
  const atis = m?.atis || data?.atis;
  const alt = computeAltitudes(m, ap);
  const moisture = moistureAssessment(m);
  const MoistIcon = moisture ? MOISTURE_ICON[moisture.icon] : null;
  const hours = (taf?.hours || []).filter((_, i) => i % 3 === 0).slice(0, 8);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="metar-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-2"><Plane className="h-5 w-5 text-lime-500" /> {title}</h3>
        {m?.code && <span className="text-xs font-bold px-2 py-1 rounded-md text-white" style={{ backgroundColor: m.code_color || "#64748b" }} data-testid="metar-flight-cat">{catLabel(m.code)}</span>}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading aviation weather…</div>
      ) : error ? (
        <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="metar-error">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {error}
        </div>
      ) : !data || !m ? (
        <p className="text-sm text-slate-400 py-4">No METAR available. Set a station (ICAO) or location.</p>
      ) : (
        <>
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <p className="text-sm font-medium text-slate-800">{ap?.name} <span className="font-mono text-slate-500">({ap?.id})</span></p>
            {data.resolved?.distance_km != null && <span className="text-xs text-slate-400">{data.resolved.distance_km} km away</span>}
          </div>

          {/* Live-style visual: compass + wind + weather glyph + category */}
          <div className="flex items-center gap-4 mb-3 bg-slate-50 border border-slate-100 rounded-lg p-3" data-testid="wx-live">
            <WxCompass dir={m.wind_dir} speed={m.wind_speed} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <WeatherGlyph keyword={m.weather_image || m.weather} isDay={m.is_day} className="h-10 w-10 text-lime-500" />
                <div>
                  {m.code && <span className="text-xs font-bold px-2 py-0.5 rounded text-white inline-block" style={{ backgroundColor: m.code_color || "#64748b" }}>{catLabel(m.code)}</span>}
                  <p className="text-sm text-slate-600 mt-0.5">{m.weather || "—"}</p>
                </div>
              </div>
              <div className="flex items-end gap-4 mt-2">
                <span className="text-3xl font-bold text-slate-900 font-heading">{m.temperature != null ? `${m.temperature}°` : "—"}</span>
                <span className="text-xs text-slate-500 pb-1">
                  {m.visibility != null && <span className="mr-3">Vis {m.visibility >= 9999 ? "10+ km" : `${(m.visibility / 1000).toFixed(m.visibility % 1000 ? 1 : 0)} km`}</span>}
                  {m.qnh != null && <span>QNH {m.qnh}</span>}
                </span>
              </div>
            </div>
          </div>

          <p className="font-mono text-xs bg-slate-900 text-lime-300 rounded-md px-3 py-2 mb-3 break-words" data-testid="metar-raw">{m.raw}</p>

          {/* Plain English decode */}
          <div className="bg-sky-50 border border-sky-100 rounded-md px-3 py-2 mb-3" data-testid="metar-plain">
            <p className="text-[11px] font-bold uppercase tracking-wider text-sky-700 mb-0.5">Plain English</p>
            <p className="text-sm text-slate-700">{plainEnglish(m)}</p>
          </div>

          {atis && (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3" data-testid="metar-atis">
              <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-0.5">ATIS {atis.code || atis.ident || ""}</p>
              <p className="font-mono text-xs text-slate-700 break-words">{atis.raw || atis.text || (typeof atis === "string" ? atis : "")}</p>
              {atis.plain && <p className="text-sm text-slate-700 mt-1">{atis.plain}</p>}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Stat icon={Wind} label="Wind" value={m.wind_speed != null ? `${m.wind_dir ?? "VRB"}° ${m.wind_speed}kt${m.wind_gust ? ` G${m.wind_gust}` : ""}` : "—"} />
            <Stat icon={Eye} label="Visibility" value={m.visibility != null ? (m.visibility >= 9999 ? "10+ km" : `${(m.visibility / 1000).toFixed(m.visibility % 1000 ? 1 : 0)} km`) : "—"} />
            <Stat icon={Thermometer} label="Temp / Dew" value={m.temperature != null ? `${m.temperature}° / ${m.dewpoint ?? "—"}°` : "—"} />
            <Stat icon={Gauge} label="QNH" value={m.qnh != null ? `${m.qnh} hPa` : "—"} />
          </div>

          {m.clouds?.length > 0 && (
            <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5"><Cloud className="h-3.5 w-3.5" /> {m.clouds.map((c) => c.height != null ? `${c.amount} ${c.height}ft` : c.report || c.amount).join(" · ")}</p>
          )}

          {moisture && (
            <div className={`mt-3 rounded-lg border px-3 py-2 flex items-start gap-2 ${MOISTURE_TONE[moisture.tone]}`} data-testid="metar-moisture">
              <MoistIcon className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider">Airborne moisture · {moisture.label}</p>
                <p className="text-sm text-slate-700 mt-0.5">{moisture.detail}</p>
              </div>
            </div>
          )}

          {alt && (
            <div className="mt-3" data-testid="metar-altitudes">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2" data-testid="metar-pressure-alt">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 flex items-center gap-1"><Mountain className="h-3.5 w-3.5" /> Pressure Alt</p>
                  <p className="text-lg font-bold text-slate-900 font-heading">{alt.pa.toLocaleString()} ft</p>
                </div>
                <div className={`rounded-lg border px-3 py-2 ${alt.da > alt.elev + 1500 ? "border-orange-200 bg-orange-50" : "border-emerald-100 bg-emerald-50"}`} data-testid="metar-density-alt">
                  <p className={`text-[11px] font-bold uppercase tracking-wider flex items-center gap-1 ${alt.da > alt.elev + 1500 ? "text-orange-700" : "text-emerald-700"}`}><Gauge className="h-3.5 w-3.5" /> Density Alt</p>
                  <p className="text-lg font-bold text-slate-900 font-heading">{alt.da.toLocaleString()} ft</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">At {ap?.id} field elevation {alt.elev.toLocaleString()} ft · QNH {m.qnh} hPa · OAT {alt.oat}°C. DA above field elevation reduces aircraft & battery performance.</p>
            </div>
          )}
          {m.observed && <p className="text-[11px] text-slate-400 mt-1">Observed {fmtTime(m.observed)} (Brisbane)</p>}

          <RunwayDiagram runways={runways} windDir={m.wind_dir} windSpeed={m.wind_speed} />

          {!compact && hours.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">TAF outlook</p>
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="taf-strip">
                {hours.map((h, i) => (
                  <div key={h.start ?? i} className="shrink-0 w-20 text-center border border-slate-200 rounded-lg p-2">
                    <p className="text-[11px] text-slate-400">{fmtTime(h.start)}</p>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white inline-block mt-1" style={{ backgroundColor: h.code_color || "#64748b" }}>{catLabel(h.code)}</span>
                    <p className="text-[11px] text-slate-600 mt-1">{h.wind_dir ?? "–"}° {h.wind_speed ?? "–"}kt</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div>
    <p className="text-xs text-slate-400 flex items-center gap-1"><Icon className="h-3.5 w-3.5" /> {label}</p>
    <p className="font-semibold text-slate-800 mt-0.5">{value}</p>
  </div>
);
